import {StyleSheet} from "aphrodite";

import {View} from "@khanacademy/wonder-blocks-core";
import {HeadingMedium} from "@khanacademy/wonder-blocks-typography";

interface Props {
    /**
     * Header text for the table view.
     */
    title?: string;
    /**
     * Subtitle node for the table view (can be a string or a React element).
     */
    subtitle?: React.ReactNode;
    /**
     * Alignment for the subtitle (start/left or end/right). Defaults to 'end'.
     */
    subtitleAlign?: "start" | "end";
}

/**
 * A component that displays a heading for the table view of Khanmigo chat history.
 * The heading can include a title and subtitle, with the subtitle defaulting to the
 * standard chat retention notice.
 * The component is aligned to the end (right in LTR layouts) and has top margin.
 *
 * @returns A React node containing the heading text wrapped in a styled View
 */
export function TableViewHeading({
    title,
    subtitle,
    subtitleAlign = "end",
}: Props): React.ReactNode {
    const headingStyles = [styles.container];
    if (title) {
        headingStyles.push(styles.spaceBetween);
    } else if (subtitleAlign === "start") {
        headingStyles.push(styles.alignStart);
    }
    return (
        <View style={headingStyles}>
            {title && <HeadingMedium>{title}</HeadingMedium>}
            {subtitle}
        </View>
    );
}

const styles = StyleSheet.create({
    container: {
        flexDirection: "row",
        alignItems: "end",
        justifyContent: "flex-end",
    },
    spaceBetween: {
        justifyContent: "space-between",
    },
    alignStart: {
        justifyContent: "flex-start",
    },
});
